import { Injectable } from '@angular/core';

import { DisplayType, TotalsPrefix } from '@cds/betting-offer';
import { EventOption, EventOptionGroup } from '@frontend/sports/betting-offer/feature/model';
import { NativePrice } from '@frontend/sports/odds/feature/native-price';
import { MarketType } from '@frontend/sports/types/components/content';

export interface Slider {
    currentValue: number;
    centerValue: number;
}

@Injectable({
    providedIn: 'root',
})
export class MarketUtilsService {
    getBalanceLine(optionGroup: EventOptionGroup): number {
        const options = optionGroup.options ?? [];
        if (options.length === 0) {
            return 0;
        }

        const handler = this.balanceLineHandlers[optionGroup.detailedGrouping.name as keyof typeof this.balanceLineHandlers];
        return handler ? handler(options) : 0;
    }

    getSteps(optionGroup: EventOptionGroup): number[] {
        const options = optionGroup.options;
        if (options.length === 0) {
            return [];
        }

        const handler = this.stepHandlers[optionGroup.detailedGrouping.name as keyof typeof this.stepHandlers];
        return handler ? handler(options) : [];
    }

    private handleSpreadBalance = (options: EventOption[]): number => {
        const teamName = this.extractTeamName(options[0]?.name ?? '');
        const teamAOptions = this.filterAndSort(
            options,
            (opt) => opt.name?.startsWith(teamName),
            'optionAttribute',
            (a, b) => +a - +b,
        );
        const teamBOptions = this.filterAndSort(
            options,
            (opt) => !opt.name?.startsWith(teamName),
            'optionAttribute',
            (a, b) => +b - +a,
        );

        return this.getMinDiffBalanceLine(
            teamAOptions.map((opt) => ({ nativePrice: opt.nativePrice, value: opt.optionAttribute })),
            teamBOptions.map((opt) => ({ nativePrice: opt.nativePrice, value: opt.optionAttribute })),
        );
    };

    private handleTotalsBalance = (options: EventOption[]): number => {
        const overOptions = this.filterOptions(options, (opt) => opt.totalsPrefix === TotalsPrefix.Over, 'marketAttribute');
        const underOptions = this.filterOptions(options, (opt) => opt.totalsPrefix === TotalsPrefix.Under, 'marketAttribute');

        return this.getMinDiffBalanceLine(
            overOptions.map((opt) => ({ nativePrice: opt.nativePrice, value: opt.marketAttribute })),
            underOptions.map((opt) => ({ nativePrice: opt.nativePrice, value: opt.marketAttribute })),
        );
    };

    private getSpreadSteps = (options: EventOption[]): number[] => {
        const teamName = this.extractTeamName(options[0]?.name ?? '');
        return this.filterOptions(options, (opt) => opt.name?.startsWith(teamName), 'optionAttribute')
            .map((opt) => +opt.optionAttribute)
            .sort((a, b) => a - b);
    };

    private getTotalsSteps = (options: EventOption[]): number[] => {
        return this.filterOptions(options, (opt) => opt.totalsPrefix === TotalsPrefix.Over, 'marketAttribute')
            .map((opt) => +opt.marketAttribute)
            .sort((a, b) => a - b);
    };

    private extractTeamName(optionName: string): string {
        const lastSpaceIndex = optionName.lastIndexOf(' ');
        return lastSpaceIndex > 0 ? optionName.slice(0, Math.max(0, lastSpaceIndex)) : optionName;
    }

    private filterOptions<T extends keyof EventOption>(
        options: EventOption[],
        predicate: (opt: EventOption) => boolean,
        attribute: T,
    ): (EventOption & Record<T, string>)[] {
        return options.filter((opt): opt is EventOption & Record<T, string> => predicate(opt) && opt[attribute] !== undefined);
    }

    private filterAndSort<T extends keyof EventOption>(
        options: EventOption[],
        predicate: (opt: EventOption) => boolean,
        attribute: T,
        sortFn: (a: string, b: string) => number,
    ): (EventOption & Record<T, string>)[] {
        return this.filterOptions(options, predicate, attribute).sort((a, b) => sortFn(a[attribute] as string, b[attribute] as string));
    }

    private getMinDiffBalanceLine(
        optionsA: { nativePrice: NativePrice; value: string | number }[],
        optionsB: { nativePrice: NativePrice; value: string | number }[],
    ): number {
        return optionsA.slice(0, optionsB.length).reduce(
            (acc, optA, i) => {
                const diff = Math.abs(optA.nativePrice.odds.toNumber() - optionsB[i].nativePrice.odds.toNumber());
                return diff < acc.minDiff ? { minDiff: diff, balanceLine: +optA.value } : acc;
            },
            { minDiff: Infinity, balanceLine: 0 },
        ).balanceLine;
    }
    private readonly balanceLineHandlers = {
        [DisplayType.Spread]: (options: EventOption[]) => this.handleSpreadBalance(options),
        [MarketType.Totals]: (options: EventOption[]) => this.handleTotalsBalance(options),
    };

    private readonly stepHandlers = {
        [DisplayType.Spread]: (options: EventOption[]) => this.getSpreadSteps(options),
        [MarketType.Totals]: (options: EventOption[]) => this.getTotalsSteps(options),
    };
}
